import { useRouter } from 'expo-router'
import { useState } from 'react'
import { Linking, Platform } from 'react-native'

import { useOtpAuthentication } from '@/src/features/auth/services/useOtpAuthentication'
import { fieldValidate } from '@/src/features/settings/utils/fieldValidate'
import { getLandingInfo } from '@/src/general/utils/cookies/landingInfos'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { usePresubscriberMutation } from '../../services/usePresubscriberMutation'
import { useOtpAuthenticationSession } from '../context/OtpAuthenticationContext'

type TOtpEmailStepOptions = {
  initialEmail?: string
  redirectTo?: string
  uik?: string
}

export const useOtpEmailStep = ({ initialEmail = '', redirectTo, uik }: TOtpEmailStepOptions) => {
  const [email, setEmail] = useState(initialEmail)
  const [hasInvalidEmail, setHasInvalidEmail] = useState(false)

  const router = useRouter()
  const { routes } = useRoutes()
  const { t } = useTranslate()
  const destination = redirectTo || routes['prayer-space']()
  const { updateSession } = useOtpAuthenticationSession()
  const { mutate: presubscribeEmail } = usePresubscriberMutation()
  const { initializationMutation, loginMethodsMutation, errors } = useOtpAuthentication(destination)
  const emailErrorKey = errors.loginMethods.emailKey

  const initializationEmailErrorKey = errors.initialization.emailKey
  const errorMessage = hasInvalidEmail
    ? t('common.form.error.email.invalid')
    : emailErrorKey
      ? t(emailErrorKey)
      : initializationEmailErrorKey
        ? t(initializationEmailErrorKey)
        : undefined
  const generalErrorKey = errors.loginMethods.generalKey || errors.initialization.generalKey

  const submitEmail = () => {
    void (async () => {
      const normalizedEmail = email.trim().toLowerCase()
      try {
        fieldValidate.email(normalizedEmail)
      } catch {
        setHasInvalidEmail(true)
        return
      }

      setEmail(normalizedEmail)
      setHasInvalidEmail(false)
      router.setParams({ email: normalizedEmail })
      initializationMutation.reset()

      const loginMethods = await loginMethodsMutation.mutateAsync(normalizedEmail)

      if (loginMethods === null) {
        updateSession({ email: normalizedEmail, name: '', mode: 'signup', csrfToken: '', nameErrorKey: undefined })
        const landingUrl = await Linking.getInitialURL()

        const landingInfos = Platform.select({
          native: {
            landingUrl: landingUrl ?? undefined,
            landingDate: new Date().toISOString(),
            // FIXME: Implement adjust to get the referrer
            referrer: undefined as string | undefined,
          },
          web: getLandingInfo(),
        })

        presubscribeEmail({
          email: email,
          referrer: Platform.OS === 'web' ? document.referrer : undefined,
          // FIXME: Implement adjust to get the sponsorKey
          sponsorKey: uik,
          ...landingInfos,
        })

        router.push(routes.authName({ redirectTo: destination }))
        return
      }

      const csrfToken = await initializationMutation.mutateAsync({ mode: 'login', email: normalizedEmail })
      updateSession({ email: normalizedEmail, name: '', mode: 'login', csrfToken, nameErrorKey: undefined })
      router.push(routes.authOtp({ redirectTo: destination }))
    })().catch(() => {})
  }

  const handleEmailChange = (value: string) => {
    setEmail(value)
    setHasInvalidEmail(false)
    initializationMutation.reset()
  }

  return {
    email,
    errorMessage,
    generalErrorKey,
    handleEmailChange,
    isPending: loginMethodsMutation.isPending || initializationMutation.isPending,
    submitEmail,
  }
}
